<template>
  <div class="left-panel" :class="{ 'collapsed': !visible }">
    <div class="side-nav" v-if="visible">
      <button class="nav-item" :class="{ active: activeTab === 'workflows' }" @click="activeTab = 'workflows'">
        <img :src="activeTab === 'workflows' ? workflowsHoverIcon : workflowsIcon" alt="工作流图标" class="nav-icon" />
        <span>工作流</span>
      </button>
      <button class="nav-item" :class="{ active: activeTab === 'dependencies' }" @click="activeTab = 'dependencies'">
        <img :src="activeTab === 'dependencies' ? dependenciesHoverIcon : dependenciesIcon" alt="依赖项图标" class="nav-icon" />      
        <span>依赖项</span>
      </button>
      <button class="nav-item" :class="{ active: activeTab === 'resources' }" @click="activeTab = 'resources'">
        <img :src="activeTab === 'resources' ? resourcesHoverIcon : resourcesIcon" alt="资源图标" class="nav-icon" />      
        <span>资源</span>
      </button>
      <button class="nav-item" :class="{ active: activeTab === 'settings' }" @click="activeTab = 'settings'">
        <img :src="activeTab === 'settings' ? settingsHoverIcon : settingsIcon" alt="设置图标" class="nav-icon" />      
        <span>设置</span>
      </button>
    </div>

    <div class="panel-content" v-if="visible">
      <div v-if="activeTab === 'workflows'">
        <WorkflowSection :expanded="true" />
      </div>

      <div v-else-if="activeTab === 'dependencies'">
        <DependencySection
          :expanded="true"
          :active-type="activeDependencyType"
          :dependencies="filteredDependencies"
          @change-type="(t) => activeDependencyType = t as any"
          @add-online="addOnlineDependency"
          @upload-custom="uploadCustomDependency"
          @load="loadDep"
          @remove="deleteDependencyHandler"
        />
      </div>

      <div v-else-if="activeTab === 'resources'">
        <ResourceSection
          :expanded="true"
          :filter="resourceFilter"
          :resources="filteredResources"
          @set-filter="(v) => resourceFilter = v"
          @upload="uploadResource"
          @create-json="createJsonResource"
          @preview="previewResource"
          @download="downloadResource"
          @delete="deleteResourceHandler"
        />
      </div>

      <div v-else>
        <VariableSection
          :expanded="true"
          :global-variables="globalVariables"
          :workflow-variables="workflowVariables"
          :auto-save="configs.autoSave"
          :show-grid="configs.showGrid"
          :snap-to-grid="configs.snapToGrid"
          :get-type-label="getTypeLabel"
          @open-add="openAddDialog"
          @open-edit="openEditDialog"
          @remove-global="deleteGlobalVariableHandler"
          @remove-workflow="deleteWorkflowVariableHandler"
          @update:auto-save="(v) => configs.autoSave = v"
          @update:show-grid="(v) => configs.showGrid = v"
          @update:snap-to-grid="(v) => configs.snapToGrid = v"
        />
      </div>
    </div>
  </div>

  <!-- Dependency Modal -->
  <DependencyModal 
    :visible="showDependencyModal"
    :is-custom-upload="isCustomUpload"
    @close="showDependencyModal = false"
    @success="onModalSuccess"
  />

  <!-- Add Variable Dialog -->
  <VariableDialog
    v-model="dialog.visible"
    :mode="dialog.mode"
    :scope="dialog.scope"
    :editing-variable="dialog.mode === 'edit' ? { key: form.key, type: form.type as any, value: form.type === 'vec2' ? form.vec2Value : form.type === 'vec3' ? form.vec3Value : form.value } : null"
    @submit="(payload) => {
      const { key, type, value } = payload as any
      const variable = { key, type, value }
      try {
        if (dialog.mode === 'add') {
          if (dialog.scope === 'global') addGlobalVariable(variable as any)
          else addWorkflowVariable(variable as any)
        } else {
          if (dialog.scope === 'global') updateGlobalVariable(variable as any)
          else updateWorkflowVariable(variable as any)
        }
        dialog.visible = false
        ElMessage.success('保存成功')
      } catch (e: any) {
        ElMessage.error(e?.message || '操作失败')
      }
    }"
  />
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue'
import DependencyModal from './DependencyModal.vue'
import WorkflowSection from './WorkflowSection.vue'
import DependencySection from './DependencySection.vue'
import ResourceSection from './ResourceSection.vue'
import VariableSection from './VariableSection.vue'
import VariableDialog from './VariableDialog.vue'
import { getDependencies, deleteDependency as deleteDep, loadDependency } from 'mflows'
import { addResourceFromFile, deleteResource, getResources as getStoreResources, createJsonResource as createJsonRes } from 'mflows'
import { variablesState, addGlobalVariable, addWorkflowVariable, updateGlobalVariable, updateWorkflowVariable, deleteGlobalVariable, deleteWorkflowVariable } from 'mflows'
import { ElMessageBox, ElMessage } from '@/plugins/elementPlus'
import workflowsIcon from '@/assets/icon/workflows.png'
import workflowsHoverIcon from '@/assets/icon/workflows-hover.png'
import dependenciesIcon from '@/assets/icon/dependencies.png'
import dependenciesHoverIcon from '@/assets/icon/dependencies-hover.png'
import resourcesIcon from '@/assets/icon/resources.png'
import resourcesHoverIcon from '@/assets/icon/resources-hover.png'
import settingsIcon from '@/assets/icon/settings.png'
import settingsHoverIcon from '@/assets/icon/settings-hover.png'

const props = defineProps<{
  visible: boolean
}>()

const activeTab = ref<'workflows' | 'dependencies' | 'resources' | 'settings'>('workflows')

const activeDependencyType = ref('umd')

const filteredDependencies = computed(() => {
  return getDependencies(activeDependencyType.value as any)
})

const showDependencyModal = ref(false)
const isCustomUpload = ref(false)

function addOnlineDependency() {
  isCustomUpload.value = false
  showDependencyModal.value = true
}

function uploadCustomDependency() {
  isCustomUpload.value = true
  showDependencyModal.value = true
}

function onModalSuccess() {
  showDependencyModal.value = false
}

function loadDep(dep: any) {
  loadDependency(dep)
}

async function deleteDependencyHandler(dep: any) {
  try { await ElMessageBox.confirm(`确定要移除依赖 "${dep.name}" 吗？`, '提示', { type: 'warning', confirmButtonText: '移除', cancelButtonText: '取消' }) } catch { return }
  deleteDep(dep.id)
}

const resourceFilter = ref('all')

const filteredResources = computed(() => {
  return getStoreResources(resourceFilter.value as any)
})

function uploadResource() {
  const input = document.createElement('input')
  input.type = 'file'
  input.multiple = true
  input.onchange = (e) => {
    const files = (e.target as HTMLInputElement).files
    if (files) {
      Array.from(files).forEach(file => {
        addResourceFromFile(file)
      })
    }
  }
  input.click()
}

function createJsonResource() {
  ElMessageBox.prompt('请输入JSON文件名:', '新建JSON', { inputValue: 'data.json', confirmButtonText: '创建', cancelButtonText: '取消' })
    .then(({ value }) => { createJsonRes(value || 'data.json') })
    .catch(() => {})
}

function previewResource(resource: any) {
  if (resource.type === 'image' && resource.url) {
    window.open(resource.url, '_blank')
  }
}

function downloadResource(resource: any) {
  if (resource.url) {
    const a = document.createElement('a')
    a.href = resource.url
    a.download = resource.name
    a.click()
  }
}

async function deleteResourceHandler(resource: any) {
  try { await ElMessageBox.confirm(`确定删除资源 "${resource.name}" 吗？`, '提示', { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' }) } catch { return }
  deleteResource(resource.id)
}

const globalVariables = computed(() => variablesState.globalVariables)
const workflowVariables = computed(() => variablesState.workflowVariables)

const configs = reactive({
  autoSave: true,
  showGrid: true,
  snapToGrid: false
})

const dialog = reactive({
  visible: false,
  mode: 'add' as 'add' | 'edit',
  scope: 'global' as 'global' | 'workflow'
})

const form = reactive({
  type: 'string',
  key: '',
  value: '',
  vec2Value: { x: 0, y: 0 },
  vec3Value: { x: 0, y: 0, z: 0 }
})

watch(() => form.type, (newType) => {
  switch (newType) {
    case 'number':
      form.value = typeof form.value === 'number' ? form.value : null
      break
    case 'boolean':
      form.value = typeof form.value === 'boolean' ? form.value : false
      break
    case 'json':
      if (typeof form.value === 'object' && form.value !== null) {
        try { form.value = JSON.stringify(form.value, null, 2) } catch { form.value = '' }
      } else if (typeof form.value !== 'string') {
        form.value = ''
      }
      break
    case 'color':
      if (typeof form.value !== 'string') form.value = ''
      break
    case 'string':
      if (typeof form.value !== 'string') form.value = ''
      break
    case 'vec2':
    case 'vec3':
      break
  }
})

function openAddDialog(scope: 'global' | 'workflow') {
  dialog.scope = scope
  dialog.mode = 'add'
  resetForm()
  dialog.visible = true
}

function openEditDialog(scope: 'global' | 'workflow', variable: any) {
  dialog.scope = scope
  dialog.mode = 'edit'
  form.type = variable.type
  form.key = variable.key

  if (variable.type === 'vec2') {
    form.vec2Value = { ...variable.value }
  } else if (variable.type === 'vec3') {
    form.vec3Value = { ...variable.value }
  } else if (variable.type === 'number') {
    form.value = typeof variable.value === 'number' ? variable.value : Number(variable.value)
  } else if (variable.type === 'json') {
    form.value = typeof variable.value === 'string' ? variable.value : JSON.stringify(variable.value, null, 2)
  } else {
    form.value = variable.value
  }
  dialog.visible = true
}

function resetForm() {
  form.type = 'string'
  form.key = ''
  form.value = ''
  form.vec2Value = { x: 0, y: 0 }
  form.vec3Value = { x: 0, y: 0, z: 0 }
}

// 已迁移到 deleteGlobalVariableHandler / deleteWorkflowVariableHandler
function getTypeLabel(type: string) {
  switch (type) {
    case 'string': return '文本'
    case 'number': return '数字'
    case 'boolean': return '布尔'
    case 'json': return 'JSON'
    case 'color': return '颜色'
    case 'vec2': return '向量2'
    case 'vec3': return '向量3'
    default: return type
  }
}

// 删除变量处理函数
async function deleteGlobalVariableHandler(variable: any) {
  try {
    await ElMessageBox.confirm(`确定删除全局变量 "${variable?.key}" 吗？`, '提示', { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' })
  } catch {
    return
  }
  deleteGlobalVariable(variable.key)
  ElMessage.success('已删除全局变量')
}

async function deleteWorkflowVariableHandler(variable: any) {
  try {
    await ElMessageBox.confirm(`确定删除工作流变量 "${variable?.key}" 吗？`, '提示', { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' })
  } catch {
    return
  }
  deleteWorkflowVariable(variable.key)
  ElMessage.success('已删除工作流变量')
}

</script>

<style scoped lang="scss">
.left-panel {
  position: fixed;
  left: 0;
  top: 40px;
  width: 290px;
  height: calc(100vh - 40px);
  background: #000000;
  // border-right: 1px solid #333;
  z-index: 1000;
  transition: transform 0.3s ease;

  &.collapsed {
    transform: translateX(-100%);
    border-right-color: transparent;
    box-shadow: none;
  }
}

.side-nav {
  position: absolute;
  left: 0;
  top: 0;
  width: 60px;
  height: 100%;
   background: #1F1F1F;
  border-right: 1px solid #313131;
  display: flex;
  flex-direction: column;
  padding: 6px 4px;
  gap: 6px;
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
  color: #bfbfbf;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px 4px;
  border-radius: 6px;
  font-size: 11px;
  justify-content: center;
}
.nav-icon { width: 16px; height: 16px; display: block; }
// .nav-item span { display: none; }
.nav-item.active, .nav-item:hover { background: #232323; color: #e6e6e6; }

// .panel-header {
//   display: flex;
//   align-items: center;
//   padding: 8px 12px;
//   background: #252526;
//   border-bottom: 1px solid #333;
// }

// .panel-title {
//   color: #cccccc;
//   font-size: 13px;
//   font-weight: 500;
// }

.panel-content {
  position: absolute;
  left: 61.5px;
  height: 100%;
  width: 227px;
  // padding: 8px 8px 8px 64px;
  display: flex;
  flex-direction: column;
  // gap: 8px;
  background-color: #1F1F1F;
  border: 1px solid #313131;
  overflow: hidden;
}
</style>
